import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { sizeGroupsFor } from '../utils/equipmentSizes';
import { useAuth } from '../contexts/AuthContext';
import { extractErrorMessage } from '../utils/apiErrors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Module-level cache so every equipment editor on a page shares one fetch.
let cache = { scope: null, rows: null, promise: null, fetchedAt: 0, loading: false, error: '' };
const listeners = new Set();
const snapshotOf = () => ({ scope: cache.scope, rows: cache.rows, loading: cache.loading, error: cache.error });
const notify = () => listeners.forEach((fn) => fn(snapshotOf()));

export const setCatalogRows = (rows, scope = cache.scope) => {
  if (scope !== cache.scope) return;
  cache.rows = rows; cache.fetchedAt = Date.now(); cache.error = ''; cache.loading = false; notify();
};

const fetchRows = () => {
  if (!cache.scope) return Promise.resolve();
  if (!cache.promise) {
    const scope = cache.scope;
    cache.loading = true; cache.error = ''; notify();
    cache.promise = axios.get(`${API}/equipment-catalog`)
      .then((res) => setCatalogRows(res.data.rows || [], scope))
      .catch((error) => { if (scope === cache.scope) { cache.error = extractErrorMessage(error, 'Equipment Catalog is unavailable. Retry to load categories.'); } })
      .finally(() => { if (scope === cache.scope) { cache.promise = null; cache.loading = false; notify(); } });
  }
  return cache.promise;
};

export const refreshEquipmentCatalog = async () => {
  const scope = cache.scope;
  // A read started before the save may contain the old catalogue. Follow it
  // with a fresh read rather than treating that stale response as the refresh.
  if (cache.promise) await cache.promise;
  if (scope === cache.scope) return fetchRows();
};

const eq = (a, b) => (a || '').trim().toLowerCase() === (b || '').trim().toLowerCase();
const uniq = (vals) => {
  const seen = new Set();
  return vals.filter((v) => { const k = v.toLowerCase(); if (!v || seen.has(k)) return false; seen.add(k); return true; })
    .sort((a, b) => a.localeCompare(b));
};

export default function useEquipmentCatalog() {
  const { user } = useAuth();
  const scope = user ? `${user.organization_id}:${user.id}` : null;
  const [snapshot, setSnapshot] = useState(snapshotOf);
  const rows = snapshot.scope === scope ? snapshot.rows : null;
  const error = snapshot.scope === scope ? snapshot.error : '';
  const loading = snapshot.scope !== scope || snapshot.loading || (rows === null && !error);

  useEffect(() => {
    listeners.add(setSnapshot);
    if (cache.scope !== scope) { cache = { scope, rows: null, promise: null, fetchedAt: 0, error: '', loading: false }; notify(); }
    else setSnapshot(snapshotOf());
    if (!cache.rows || Date.now() - cache.fetchedAt > 20_000) fetchRows();
    const onFocus = () => fetchRows();
    window.addEventListener('focus', onFocus);
    return () => { listeners.delete(setSnapshot); window.removeEventListener('focus', onFocus); };
  }, [scope]);

  const refresh = useCallback(() => refreshEquipmentCatalog(), []);
  const setRows = useCallback((value) => setCatalogRows(value, scope), [scope]);

  return useMemo(() => {
    const all = rows || [];
    const roots = all.filter((row) => row.type_label);
    const allTypes = roots.map((row) => ({ value: row.equipment_type, label: row.type_label, hidden: row.type_hidden }))
      .sort((a, b) => a.label.localeCompare(b.label));
    const types = allTypes.filter((type) => !type.hidden);
    return {
    rows: all,
    types,
    allTypes,
    labelForType: (value) => allTypes.find((type) => type.value === value)?.label || value,
    loading,
    error,
    refresh,
    setRows,
    sizeGroupsFor: (type, style, make, model) => sizeGroupsFor(all, type, style, make, model),
    stylesFor: (type) => uniq(all.filter((r) => r.equipment_type === type).map((r) => r.style)),
    manufacturersFor: (type, style) => {
      const inType = all.filter((r) => r.equipment_type === type);
      const scoped = style ? inType.filter((r) => eq(r.style, style)) : inType;
      return uniq(scoped.map((r) => r.manufacturer));
    },
    modelsFor: (type, style, manufacturer) => {
      let scoped = all.filter((r) => r.equipment_type === type);
      if (style) scoped = scoped.filter((r) => eq(r.style, style));
      if (manufacturer && scoped.some((r) => eq(r.manufacturer, manufacturer))) scoped = scoped.filter((r) => eq(r.manufacturer, manufacturer));
      else if (manufacturer) return [];
      return uniq(scoped.map((r) => r.model));
    },
    };
  }, [rows, refresh, setRows, loading, error]);
}
